/* Attendance.jsx */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Attendance.css";

function Attendance() {
  const [attendanceList, setAttendanceList] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(true);
  const [classList, setClassList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState("");

  // 7 days back limit
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const sevenDaysAgoString = sevenDaysAgo.toISOString().split("T")[0];

  /* ---------- API ---------- */
  const fetchAttendanceData = async (date) => {
    setLoading(true);
    try {
      const teacherPhone = localStorage.getItem("userPhone");
      if (!teacherPhone) {
        toast.error("Teacher phone number is not available.");
        setLoading(false);
        return;
      }

      const { data } = await axios.get(
        `https://aadyaacademy.org/api/teacherattendance?date=${date}&phone=${teacherPhone}`
      );

      if (data.attendance?.length) {
        const valid = data.attendance
          .filter(
            (s) =>
              s.student_id && s.student_name && s.attendance_date
          )
          .sort((a, b) => a.student_name.localeCompare(b.student_name));

        setAttendanceList(valid);

        const classes = [...new Set(valid.map((s) => s.class))];
        const sections = [...new Set(valid.map((s) => s.section))];
        setClassList(classes);
        setSectionList(sections);
      } else {
        setAttendanceList([]);
        setClassList([]);
        setSectionList([]);
        toast.info("No attendance data for the selected date.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load attendance data.");
      setAttendanceList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (e) => {
    const d = e.target.value;
    setSelectedDate(d);
    fetchAttendanceData(d);
  };

  const handleAttendanceChange = (studentId, newStatus) => {
    setAttendanceList((prev) =>
      prev.map((s) =>
        s.student_id === studentId
          ? { ...s, attendance_status: newStatus }
          : s
      )
    );
  };

  const handleSaveAttendance = async () => {
    if (!selectedDate) return toast.error("Select a date.");
    const teacherPhone = localStorage.getItem("userPhone");
    if (!teacherPhone) return toast.error("Teacher phone missing.");

    if (!attendanceList.length)
      return toast.error("No attendance data to save.");

    try {
      // check if already saved
      const { data } = await axios.get(
        `https://aadyaacademy.org/api/teacher_attendance_no_default?date=${selectedDate}&phone=${teacherPhone}`
      );
      if (data.attendance?.length) {
        if (!window.confirm("Attendance already exists. Update?")) return;
      }

      const payload = attendanceList.map((s) => ({
        student_id: s.student_id,
        attendance_status: s.attendance_status,
        attendance_date: selectedDate,
      }));

      await axios.post("https://aadyaacademy.org/api/attendance", {
        date: selectedDate,
        attendance_data: payload,
      });

      toast.success("Attendance saved!");
    } catch (err) {
      console.error(err);
      toast.error("Save failed.");
    }
  };

  const formatDate = (d) => {
    const nd = new Date(d);
    return `${nd.getDate()}/${nd.getMonth() + 1}/${nd.getFullYear()}`;
  };

  const filteredAttendance = attendanceList
    .filter((s) => {
      return (
        (!classFilter || s.class === classFilter) &&
        (!sectionFilter || s.section === sectionFilter) &&
        (!attendanceFilter || s.attendance_status === attendanceFilter)
      );
    })
    .sort((a, b) => a.student_name.localeCompare(b.student_name));

  return (
    <div className="attendance">
      <ToastContainer />
      <div className="attendance-table">
        {/* Header */}
        <div className="table-header">
          <div className="table-header-top">
            <h2>Attendance</h2>
            <p className="status-hint">
              Present - P, Absent - A, Half Day - H
            </p>
          </div>
          <div className="table-header-btm">
            <input
              type="date"
              className="date-picker"
              value={selectedDate}
              max={new Date().toISOString().split("T")[0]}
              min={sevenDaysAgoString}
              onChange={handleDateChange}
            />
            <select
              className="class-filter"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">Class</option>
              {classList.map((c, i) => (
                <option key={i} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              className="section-filter"
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
            >
              <option value="">Section</option>
              {sectionList.map((s, i) => (
                <option key={i} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              className="attendance-filter"
              value={attendanceFilter}
              onChange={(e) => setAttendanceFilter(e.target.value)}
            >
              <option value="">Status</option>
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Half Day">Half Day</option>
            </select>
          </div>
        </div>

        {/* Scrollable Table */}
        <div className="table-scroll-wrapper">
          {loading ? (
            <div className="loading">Loading…</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Section</th>
                  <th>Date</th>
                  <th>Attendance</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttendance.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-records">
                      No records match the selected filters.
                    </td>
                  </tr>
                ) : (
                  filteredAttendance.map((student) => (
                    <tr key={student.student_id}>
                      <td>{student.student_id}</td>
                      <td>{student.student_name}</td>
                      <td>{student.class}</td>
                      <td>{student.section}</td>
                      <td>{formatDate(student.attendance_date)}</td>
                      <td className="attendance-cell">
                        {["Present", "Absent", "Half Day"].map((st) => (
                          <span
                            key={st}
                            onClick={() =>
                              handleAttendanceChange(student.student_id, st)
                            }
                            className={`attendance-option ${
                              student.attendance_status === st ? "selected" : ""
                            }`}
                            style={{
                              backgroundColor:
                                student.attendance_status === st
                                  ? st === "Present"
                                    ? "#28a745"
                                    : st === "Absent"
                                    ? "#dc3545"
                                    : "#ffc107"
                                  : "transparent",
                            }}
                          >
                            {st === "Present" ? "P" : st === "Absent" ? "A" : "H"}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Sticky Save Button */}
        <div className="save-attendance">
          <button className="save-attendance-btn" onClick={handleSaveAttendance}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default Attendance;